import { db } from "@/lib/dexie-db";

const REFRESH_MS = 5 * 60 * 1000;
const IDB_KEY = "latest";

export type SdoHmiImageState = {
    url: string;
    ready: boolean;
    failed: boolean;
    /** True while a newer image is downloading but an older image is still shown. */
    refreshing: boolean;
};

type PreloadEntry = {
    loadingUrl: string;
    displayUrl: string;
    ready: boolean;
    failed: boolean;
    promise: Promise<boolean>;
};

let entry: PreloadEntry | null = null;
/** Last successfully loaded display URL (object URL or Convex storage URL). */
let lastReadyDisplayUrl: string | null = null;
let objectUrlToRevoke: string | null = null;
let hydratePromise: Promise<void> | null = null;
const listeners = new Set<(state: SdoHmiImageState) => void>();

function currentState(): SdoHmiImageState {
    if (entry) {
        const hasPrevious = Boolean(lastReadyDisplayUrl);
        return {
            url: entry.displayUrl,
            ready: entry.ready || hasPrevious,
            failed: entry.failed && !hasPrevious,
            refreshing: !entry.ready && hasPrevious,
        };
    }
    if (lastReadyDisplayUrl) {
        return {
            url: lastReadyDisplayUrl,
            ready: true,
            failed: false,
            refreshing: false,
        };
    }
    return {
        url: "",
        ready: false,
        failed: false,
        refreshing: false,
    };
}

function notifyListeners() {
    const state = currentState();
    for (const listener of listeners) {
        listener(state);
    }
}

function setReadyDisplayUrl(url: string) {
    lastReadyDisplayUrl = url;
}

async function readPersistedImage(): Promise<{ blob: Blob } | null> {
    try {
        const row = await db.sdoHmiImage.get(IDB_KEY);
        if (!row?.blob || !(row.blob instanceof Blob)) return null;
        return { blob: row.blob };
    } catch {
        return null;
    }
}

async function writePersistedImage(blob: Blob, bucket: number): Promise<void> {
    try {
        await db.sdoHmiImage.put({
            id: IDB_KEY,
            blob,
            bucket,
            savedAt: Date.now(),
        });
    } catch {
        // Quota / private mode — ignore.
    }
}

async function persistFetchedImage(networkUrl: string): Promise<string | null> {
    try {
        const response = await fetch(networkUrl, { mode: "cors", cache: "force-cache" });
        if (!response.ok) return null;
        const blob = await response.blob();
        if (blob.size < 1000) return null;
        await writePersistedImage(blob, Math.floor(Date.now() / REFRESH_MS));
        const objectUrl = URL.createObjectURL(blob);
        if (objectUrlToRevoke && objectUrlToRevoke !== lastReadyDisplayUrl) {
            URL.revokeObjectURL(objectUrlToRevoke);
        }
        objectUrlToRevoke = objectUrl;
        return objectUrl;
    } catch {
        return null;
    }
}

function preloadUrl(url: string): PreloadEntry {
    const displayWhileLoading = lastReadyDisplayUrl ?? url;
    const preloadEntry: PreloadEntry = {
        loadingUrl: url,
        displayUrl: displayWhileLoading,
        ready: Boolean(lastReadyDisplayUrl),
        failed: false,
        promise: new Promise<boolean>((resolve) => {
            const img = new Image();
            img.onload = () => {
                void (async () => {
                    const localUrl = await persistFetchedImage(url);
                    const displayUrl = localUrl ?? url;
                    setReadyDisplayUrl(displayUrl);
                    preloadEntry.displayUrl = displayUrl;
                    preloadEntry.ready = true;
                    preloadEntry.failed = false;
                    notifyListeners();
                    resolve(true);
                })();
            };
            img.onerror = () => {
                preloadEntry.failed = !lastReadyDisplayUrl;
                if (lastReadyDisplayUrl) {
                    preloadEntry.displayUrl = lastReadyDisplayUrl;
                    preloadEntry.ready = true;
                }
                notifyListeners();
                resolve(Boolean(lastReadyDisplayUrl));
            };
            img.src = url;
        }),
    };
    return preloadEntry;
}

/** Restore the last saved disc image from IndexedDB before network refresh. */
export function hydrateSdoHmiImageFromDisk(): Promise<void> {
    if (hydratePromise) return hydratePromise;
    hydratePromise = (async () => {
        const row = await readPersistedImage();
        if (!row || lastReadyDisplayUrl) return;
        const objectUrl = URL.createObjectURL(row.blob);
        objectUrlToRevoke = objectUrl;
        setReadyDisplayUrl(objectUrl);
        if (!entry) {
            entry = {
                loadingUrl: objectUrl,
                displayUrl: objectUrl,
                ready: true,
                failed: false,
                promise: Promise.resolve(true),
            };
        } else if (!entry.ready) {
            entry.displayUrl = objectUrl;
            entry.ready = true;
        }
        notifyListeners();
    })();
    return hydratePromise;
}

/**
 * Load / refresh the SDO disc image from a Convex-hosted URL.
 * Never pass a NASA URL here — the server caches NASA and exposes Convex storage URLs.
 */
export function ensureSdoHmiImageCached(
    convexImageUrl: string | null | undefined,
    force = false,
): Promise<SdoHmiImageState> {
    return hydrateSdoHmiImageFromDisk().then(() => {
        if (!convexImageUrl) {
            if (!lastReadyDisplayUrl) {
                entry = {
                    loadingUrl: "",
                    displayUrl: "",
                    ready: false,
                    failed: true,
                    promise: Promise.resolve(false),
                };
                notifyListeners();
            }
            return currentState();
        }

        if (!force && entry?.loadingUrl === convexImageUrl && entry.ready) {
            return entry.promise.then(() => currentState());
        }

        entry = preloadUrl(convexImageUrl);
        notifyListeners();
        return entry.promise.then(() => currentState());
    });
}

export function getSdoHmiImageState(): SdoHmiImageState | null {
    if (!entry && !lastReadyDisplayUrl) return null;
    return currentState();
}

export function subscribeSdoHmiImage(listener: (state: SdoHmiImageState) => void): () => void {
    listeners.add(listener);
    listener(currentState());
    return () => {
        listeners.delete(listener);
    };
}
